// packages imports
import { useEffect, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { Autocomplete, TextField } from '@mui/material'
import { useDebounce } from 'use-debounce'

// others
import { AccountType, FetchAllAccountsQuery, useFetchAllAccountsLazyQuery } from '../../../../../generated'
import { GRAPHQL_QUERY_POLICY_CACHE_FIRST } from '../../../../constants'

interface BankAccountsListProps {
  name: string
  title: string
  type?: AccountType
}

const BankAccountsList = ({ name, title, type }: BankAccountsListProps) => {
  const [accounts, setAccounts] = useState<FetchAllAccountsQuery['fetchAllAccounts']['accounts']>([])
  const [searchTerm, setSearchTerm] = useState<string>('')
  const { control } = useFormContext()

  const [debouncedSearchTerm] = useDebounce(searchTerm, 500)
  const options =
    accounts?.map(account => ({
      id: account?.id || '',
      name: account?.name || ''
    })) || []

  const [fetchAccounts, { loading }] = useFetchAllAccountsLazyQuery({
    ...GRAPHQL_QUERY_POLICY_CACHE_FIRST,
    variables: undefined,
    onError() {},
    onCompleted(data) {
      if (data?.fetchAllAccounts?.accounts?.length) {
        setAccounts(data.fetchAllAccounts.accounts)
      }
    }
  })

  useEffect(() => {
    fetchAccounts({
      variables: {
        accountsInput: {
          isBankAccount: true,
          name: debouncedSearchTerm,
          type
        }
      }
    })
  }, [fetchAccounts, debouncedSearchTerm, type])

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error: { message } = {}, invalid } }) => {
        const value = options.find(account => account?.id === field.value) || null

        return (
          <>
            <Autocomplete
              {...field}
              options={options}
              value={value}
              loading={loading}
              onChange={(_, newValue) => field.onChange(newValue?.id || null)}
              onInputChange={(_, newInputValue) => setSearchTerm(newInputValue)}
              getOptionLabel={option => option?.name}
              autoHighlight
              autoSelect
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              renderInput={params => (
                <TextField {...params} label={title} error={!!invalid} helperText={!!invalid ? message : undefined} />
              )}
            />
          </>
        )
      }}
    />
  )
}

export default BankAccountsList
